import { GameScoreBreakdown, NflPlayerGameStatistics, Passing, PassingScoringSettings, SettingWithBonuses, KickReturns, SpecialTeamsScoringSettings, PuntReturns, Fumbles, Player, PlayerGameScore, Receiving, ReceivingScoringSettings, Rushing, RushingScoringSettings, ScoringSettings, Statistic, NflGame } from "../../sdk/model";


const ALL_STRING : string = "ALL";
const SECONDS_IN_QUARTER : number = 15 * 60;
const SECONDS_IN_GAME : number = SECONDS_IN_QUARTER * 4;

export function getTotalScoreFromStats(stats: Statistic | undefined, scoringSettings: ScoringSettings, key : string, playerScoreForBreakdown: PlayerGameScore | undefined) : number | undefined {
  if (!stats) {
    return undefined;
  }

  let passing: number = 0;
  if (stats.passing) {
    passing = getPassingScore(getSettingsWithKey(scoringSettings.passingScoringSettings, key), stats.passing ,playerScoreForBreakdown);
  }

  let rushing: number = 0;
  if (stats.rushing) {
    rushing = getRushingScore(getSettingsWithKey(scoringSettings.rushingScoringSettings, key), stats.rushing, playerScoreForBreakdown);
  }

  let receiving: number = 0;
  if (stats.receiving) {
    receiving = getReceivingScore(getSettingsWithKey(scoringSettings.receivingScoringSettings, key), stats.receiving, playerScoreForBreakdown);
  }
  
  let kickReturns: number = 0;
  if (stats.kickReturns) {
    kickReturns = getKickReturnsScore(scoringSettings.specialTeamsSettings, stats.kickReturns, playerScoreForBreakdown);
  }
  
  let puntReturns: number = 0;
    if (stats.puntReturns) {
      puntReturns = getPuntReturnsScore(scoringSettings.specialTeamsSettings, stats.puntReturns, playerScoreForBreakdown);
    }
  
  let fumbles: number = 0;
  if (stats.fumbles)  {
    fumbles = getFumblesScore(scoringSettings.fumbles, stats.fumbles, playerScoreForBreakdown);
  } 
  return round(passing + rushing + receiving + kickReturns + puntReturns + fumbles);
}

export function getPlayerCurrentScoreFromStats(player: Player, scoringSettings: ScoringSettings, nflGameMap: Map<string, NflGame> | undefined) : PlayerGameScore {
  return getPlayerScoreFromStats(player, scoringSettings, player.quick_stats?.currentWeeksStats || {}, nflGameMap);
}

function getTotalSecondsRemaining(game : NflGame) {
  if (game && game.clock) {
    const minuteSecondSplit = game.clock?.split(':')!;
    const secondsRemainingInQuarter = Number(minuteSecondSplit[0]) * 60 + Number(minuteSecondSplit[1]);
    const fullQuartersRemaining = 4 - game.quarter!;
    return Math.max(secondsRemainingInQuarter + fullQuartersRemaining * SECONDS_IN_QUARTER, 0);
  }
  return SECONDS_IN_GAME;
}

export function getPlayerScoreFromStats(player: Player ,scoringSettings: ScoringSettings, playerStats: NflPlayerGameStatistics, nflGameMap: Map<string, NflGame> | undefined) : PlayerGameScore {
  let playerScore: PlayerGameScore = {};
  const key : string = (player.position ?? "ALL").toString().toUpperCase();
  playerScore.playerId = player.id;
  playerScore.totalScore = getTotalScoreFromStats(playerStats.statistics, scoringSettings, key, playerScore);
  const originalProjection = getTotalScoreFromStats(playerStats.projections, scoringSettings, key, undefined)
  const game = nflGameMap?.get(player.quick_stats?.currentWeeksStats?.gameId!)!;
  playerScore.originalProjectedScore = originalProjection;
  if (!originalProjection) {
    playerScore.projectedScore = playerScore.totalScore;
  } else if (!nflGameMap) {
    playerScore.projectedScore = originalProjection;
  } else if (game && game.status === 'COMPLETE') {
    playerScore.projectedScore = playerScore.totalScore;
  } else {
    const currentScore = playerScore.totalScore ? playerScore.totalScore : 0;
    const projectedPointsPerSecond = originalProjection / SECONDS_IN_GAME;
    const totalSecondsRemaining = getTotalSecondsRemaining(game);
    playerScore.projectedScore = currentScore + projectedPointsPerSecond * totalSecondsRemaining;
  }
  return playerScore;
 

}

function getPassingScore(scoringSettings: PassingScoringSettings, passingStatistics: Passing, playerScore: PlayerGameScore | undefined) : number {
  let score = 0;

  if (!passingStatistics) {
    return score;
  }

  let yards : number = (passingStatistics?.yards ?? 0) / (scoringSettings?.yardsPerPoint ?? 1) * (scoringSettings?.pointsPer ?? 0);
  populateScoreBreakdown({
    playerScore,
    statType: "Passing Yards",
    stat: passingStatistics?.yards,
    pointsPer: scoringSettings?.pointsPer,
    yardsPerPoint: scoringSettings?.yardsPerPoint,
    pointsTotal: yards
  });

  let bonusYards : number = 0;
  if(scoringSettings?.bonusYards && scoringSettings?.bonusYardsPoints
      && passingStatistics?.yards && passingStatistics?.yards > scoringSettings?.bonusYards){
        bonusYards = scoringSettings?.bonusYardsPoints;
        populateScoreBreakdown({
          playerScore,
          statType: "Passing Bonus",
          stat: undefined,
          pointsPer: scoringSettings.bonusYards,
          pointsTotal: bonusYards
        });
  }

  const tds: number = (passingStatistics?.touchdowns ?? 0) * (scoringSettings?.tds ?? 0);
  populateScoreBreakdown({
    playerScore,
    statType: "Passing TDs",
    stat: passingStatistics?.touchdowns,
    pointsPer: scoringSettings.tds,
    pointsTotal: tds
  });

  const conversions: number = (passingStatistics?.conversions ?? 0) * (scoringSettings?.twoPointConversions ?? 0);
  populateScoreBreakdown({
    playerScore,
    statType: "Passing Conversions",
    stat: passingStatistics?.conversions,
    pointsPer: scoringSettings.twoPointConversions,
    pointsTotal: conversions
  });

  const ints : number = (passingStatistics?.interceptions ?? 0) * (scoringSettings?.interceptions ?? 0);
  populateScoreBreakdown({
    playerScore,
    statType: "Interceptions",
    stat: passingStatistics?.interceptions,
    pointsPer: scoringSettings.interceptions,
    pointsTotal: ints
  });

  const sacks : number = (passingStatistics?.sacks ?? 0) * (scoringSettings?.sacked ?? 0);
  populateScoreBreakdown({
    playerScore,
    statType: "Sacks",
    stat: passingStatistics?.sacks,
    pointsPer: scoringSettings.sacked,
    pointsTotal: sacks
  });

  const firstDowns : number = (passingStatistics?.firstDowns ?? 0) * (scoringSettings?.firstDowns ?? 0);
  populateScoreBreakdown({
    playerScore,
    statType: "Passing First Downs",
    stat: passingStatistics?.firstDowns,
    pointsPer: scoringSettings.firstDowns,
    pointsTotal: firstDowns
  });

  score += yards + bonusYards + tds + conversions + ints + sacks + firstDowns;
  return score;
}

function getRushingScore(scoringSettings: RushingScoringSettings, rushingStatistics: Rushing, playerScore: PlayerGameScore | undefined) {
  let score = 0;

  if (!rushingStatistics) {
    return score;
  }

  let yards : number = (rushingStatistics?.yards ?? 0) / (scoringSettings?.yardsPerPoint ?? 1) * (scoringSettings?.pointsPer ?? 0);
  populateScoreBreakdown({
    playerScore,
    statType: "Rushing Yards",
    stat: rushingStatistics?.yards,
    pointsPer: scoringSettings?.pointsPer,
    yardsPerPoint: scoringSettings?.yardsPerPoint,
    pointsTotal: yards
  });

  let bonusYards : number = 0;
  if(scoringSettings?.bonusYards && scoringSettings?.bonusYardsPoints
      && rushingStatistics?.yards && rushingStatistics?.yards > scoringSettings?.bonusYards){
        bonusYards = scoringSettings?.bonusYardsPoints;
        populateScoreBreakdown({
          playerScore,
          statType: "Rushing Bonus",
          stat: undefined,
          pointsPer: scoringSettings.bonusYards,
          pointsTotal: bonusYards
        });
  }

  const tds: number = (rushingStatistics?.touchdowns ?? 0) * (scoringSettings?.tds ?? 0);
  populateScoreBreakdown({
    playerScore,
    statType: "Rushing TDs",
    stat: rushingStatistics?.touchdowns,
    pointsPer: scoringSettings.tds,
    pointsTotal: tds
  });

  const conversions: number = (rushingStatistics?.conversions ?? 0) * (scoringSettings?.twoPointConversions ?? 0);
  populateScoreBreakdown({
    playerScore,
    statType: "Rushing Conversions",
    stat: rushingStatistics?.conversions,
    pointsPer: scoringSettings.twoPointConversions,
    pointsTotal: conversions
  });

  const firstDowns : number = (rushingStatistics?.firstDowns ?? 0) * (scoringSettings?.firstDowns ?? 0);  
  populateScoreBreakdown({
    playerScore,
    statType: "Rushing First Downs",
    stat: rushingStatistics?.firstDowns,
    pointsPer: scoringSettings.firstDowns,
    pointsTotal: firstDowns
  });

  score += yards + bonusYards + tds + conversions + firstDowns;

  return score;
}

function getReceivingScore(scoringSettings: ReceivingScoringSettings, rushingStatistics: Receiving, playerScore: PlayerGameScore | undefined) {
  let score = 0;

  if (!rushingStatistics) {
    return score;
  }

  let yards : number = (rushingStatistics?.yards ?? 0) / (scoringSettings?.yardsPerPoint ?? 1) * (scoringSettings?.pointsPer ?? 0);
  populateScoreBreakdown({
    playerScore,
    statType: "Receiving Yards",
    stat: rushingStatistics?.yards,
    pointsPer: scoringSettings?.pointsPer,
    yardsPerPoint: scoringSettings?.yardsPerPoint,
    pointsTotal: yards
  });

  let bonusYards : number = 0;
  if(scoringSettings?.bonusYards && scoringSettings?.bonusYardsPoints
      && rushingStatistics?.yards && rushingStatistics?.yards > scoringSettings?.bonusYards){
        bonusYards = scoringSettings?.bonusYardsPoints;
        populateScoreBreakdown({
          playerScore,
          statType: "Receiving Bonus",
          stat: undefined,
          pointsPer: scoringSettings.bonusYards,
          pointsTotal: bonusYards
        });
  }

  const tds: number = (rushingStatistics?.touchdowns ?? 0) * (scoringSettings?.tds ?? 0);
  populateScoreBreakdown({
    playerScore,
    statType: "Receiving TDs",
    stat: rushingStatistics?.touchdowns,
    pointsPer: scoringSettings.tds,
    pointsTotal: tds
  });

  const receptions: number = (rushingStatistics?.receptions ?? 0) * (scoringSettings?.receptions ?? 0);
  populateScoreBreakdown({
    playerScore,
    statType: "Receptions",
    stat: rushingStatistics?.receptions,
    pointsPer: scoringSettings.receptions,
    pointsTotal: receptions
  });

  const conversions: number = (rushingStatistics?.conversions ?? 0) * (scoringSettings?.twoPointConversions ?? 0);
  populateScoreBreakdown({
    playerScore,
    statType: "Receiving Conversions",
    stat: rushingStatistics?.conversions,
    pointsPer: scoringSettings.twoPointConversions,
    pointsTotal: conversions
  });

  const firstDowns : number = (rushingStatistics?.firstDowns ?? 0) * (scoringSettings?.firstDowns ?? 0);  
  populateScoreBreakdown({
    playerScore,
    statType: "Receiving First Downs",
    stat: rushingStatistics?.firstDowns,
    pointsPer: scoringSettings.firstDowns,
    pointsTotal: firstDowns
  });

  score += yards + bonusYards + tds + receptions + conversions + firstDowns;

  return score;


}

function getKickReturnsScore(scoringSettings: SpecialTeamsScoringSettings, kickReturns: KickReturns, playerScore: PlayerGameScore | undefined) {
  let score = 0;
  
  if (!kickReturns) {
    return score;
  }
  
  let kickReturnTds : number = (kickReturns.touchdowns ?? 0) * (scoringSettings?.kickoffReturnTds.points ?? 0);
  populateScoreBreakdown({
    playerScore,
    statType: 'Kickoff Return TDs',
    stat: kickReturns?.touchdowns,
    pointsPer: scoringSettings?.kickoffReturnTds?.points,
    pointsTotal: kickReturnTds
  });
  
  return kickReturnTds;
}

function getPuntReturnsScore(scoringSettings: SpecialTeamsScoringSettings, puntReturns: PuntReturns, playerScore: PlayerGameScore | undefined) {
  let score = 0;
  
  if (!puntReturns) {
    return score;
  }
  
  let puntReturnTds : number = (puntReturns.touchdowns ?? 0) * (scoringSettings?.puntReturnTds.points ?? 0);
  populateScoreBreakdown({
    playerScore,
    statType: 'Punt Return TDs',
    stat: puntReturns?.touchdowns,
    pointsPer: scoringSettings?.puntReturnTds?.points,
    pointsTotal: puntReturnTds
  });
  
  return puntReturnTds;
}

function getFumblesScore(scoringSettings: SettingWithBonuses, fumbles: Fumbles, playerScore: PlayerGameScore | undefined) {
  let score = 0;

  if (!fumbles) {
    return score;
  }

  let lostFumbles : number = (fumbles?.lostFumbles ?? 0) * (scoringSettings?.points ?? 0);
  populateScoreBreakdown({
    playerScore,
    statType: "Fumbles Lost",
    stat: fumbles?.lostFumbles,
    pointsPer: scoringSettings?.points,
    pointsTotal: lostFumbles
  });

  return lostFumbles;
}

interface ScoreBreakdownParams {
  playerScore: PlayerGameScore | undefined;
  statType: string;
  stat: number | undefined;
  pointsPer: number | undefined;
  yardsPerPoint?: number | undefined;
  pointsTotal: number;
}

function populateScoreBreakdown(params: ScoreBreakdownParams) {
  const { playerScore, statType, stat, pointsPer, yardsPerPoint, pointsTotal } = params;
  
  if (!playerScore) {
    return;
  }
  if(!playerScore.scoreBreakdown){
    playerScore.scoreBreakdown = [];
  }
  if(!stat){
    return;
  }
  
  // Normalize pointsPer when yardsPerPoint is provided
  let normalizedPointsPer = pointsPer;
  if (yardsPerPoint && yardsPerPoint > 0 && pointsPer !== undefined) {
    normalizedPointsPer = pointsPer / yardsPerPoint;
  }
  
  let scoreBreakdown: GameScoreBreakdown = {}
  scoreBreakdown.statType = statType;
  scoreBreakdown.stat = stat;
  scoreBreakdown.pointsPer = normalizedPointsPer;
  scoreBreakdown.points = round(pointsTotal);
  playerScore.scoreBreakdown.push(scoreBreakdown);
}

// Utility Method

function getSettingsWithKey(scoringSettings: { [key: string]: any; }, key: string) {
  if(!scoringSettings) {
    console.log("No scoring settings found");
    return {};
  }
  if (scoringSettings[key]) {
    return scoringSettings[key];
  }
  return scoringSettings[ALL_STRING];
}

function round(value: number): number {
  return parseFloat(value.toFixed(2));

}


/**
 * Shouldn't be exported into production
 */
export const exportedForTesting = {
  getPassingScore
}
